import { Button, Message } from "@/ui-kit";
import { useTranslation } from "react-i18next";
import { InputControl, InputPasswordControl } from "@/components/controls";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { ROUTES } from "@/router";
import { FormView } from "../FormView";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuthContext } from "@/pages/Auth";
import { UsersRepository } from "@/api/users/repository";
import { UsersService } from "@/api/users/service";
import type { AxiosError } from "axios";
import { AuthRepository } from "@/api/auth/repository";
import { AuthService } from "@/api/auth/service";
import type { ILoginResponse } from "@/api/auth/types";
import { phoneSchema } from "@/utils/validation";
import { PhoneControl } from "@/components/controls/PhoneControl";
import type { IServerError } from "@/types/common";
import { handleSuccessfulAuthentication } from "../../utils";

type IForm = {
  id: string;
  token: string;
  userName: string;
  email: string;
  password: string;
  password_repeat: string;
  fullName: string;
  phone: string;
};

const SignUp = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const { translations } = useAuthContext();

  const usersRepository = useMemo(() => {
    return new UsersRepository(new UsersService());
  }, []);

  const authRepository = useMemo(() => {
    return new AuthRepository(new AuthService());
  }, []);

  const schema = useMemo(() => {
    return z
      .object({
        email: z
          .string()
          .min(1, { message: t("validation.required") })
          .email({ message: t("validation.invalid_email") }),
        id: z.string().min(1, { message: t("validation.required") }),
        token: z.string().min(1, { message: t("validation.required") }),
        fullName: z.string().min(1, { message: t("validation.required") }),
        userName: z.string().min(1, { message: t("validation.required") }),
        password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_too_short") }),
        password_repeat: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_too_short") }),
        phone: phoneSchema,
      })
      .refine((data) => data.password === data.password_repeat, {
        message: t("validation.passwords_must_match"),
        path: ["password_repeat"],
      });
  }, [t]);

  const defaultValues = useMemo((): IForm => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get("email") || "";
    const fullName = params.get("fullName") || "";
    const id = params.get("id") || "";
    const token = params.get("token") || "";

    return {
      id,
      token,
      email,
      fullName,
      userName: email,
      password: "",
      password_repeat: "",
      phone: "",
    };
  }, []);

  const form = useForm<IForm>({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const { handleSubmit, formState } = form;

  const onSubmit = useCallback(
    (formData: IForm) => {
      setLoading(true);

      const params = new URLSearchParams(window.location.search);
      const tenant = params.get("tenant") || "";

      usersRepository
        .claimInvite(formData)
        .then(() => {
          return authRepository.login({
            username: formData.userName,
            password: formData.password,
          });
        })
        .then((response: ILoginResponse) => {
          return handleSuccessfulAuthentication(
            response.accessToken,
            response.refreshToken,
            tenant,
          );
        })
        .catch((error: AxiosError<IServerError>) => {
          setMessage(error?.response?.data?.error || error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [usersRepository, authRepository],
  );

  return (
    <FormProvider {...form}>
      <FormView
        title={translations?.signup_welcome_title}
        subtitle={translations?.signup_welcome_subtitle}
      >
        <form
          className="nuco-auth__common-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <InputControl
            name="email"
            size="lg"
            label={t("label.email")}
            placeholder={t("placeholder.email")}
            invalid={!!formState.errors.email}
            color="layer2"
            disabled={true}
          />

          <InputControl
            name="fullName"
            size="lg"
            label={t("label.full_name")}
            placeholder={t("placeholder.full_name")}
            invalid={!!formState.errors.fullName}
            color="layer2"
          />

          <PhoneControl name="phone" color="layer2" />

          <InputPasswordControl
            name="password"
            size="lg"
            label={t("label.password")}
            placeholder={t("placeholder.password")}
            invalid={!!formState.errors.password}
            color="layer2"
          />

          <InputPasswordControl
            name="password_repeat"
            size="lg"
            label={t("label.repeat_password")}
            placeholder={t("placeholder.password")}
            invalid={!!formState.errors.password_repeat}
            color="layer2"
          />

          {message && (
            <div className="nuco-auth-common-form__message">
              <Message text={message} variant="red" />
            </div>
          )}

          <div className="nuco-auth__common-form-actions">
            <Button
              type="submit"
              size="lg"
              content={t("action.complete_registration")}
              loading={loading}
            />
          </div>
        </form>

        <div className="nuco-auth__common-footer">
          <div className="nuco-auth__common-footer-message nuco-typography--button-md">
            <span>{t("label.have_account_message")}</span>
            <Link
              to={`/${ROUTES.AUTH}/${ROUTES.SIGN_IN}`}
              className="nuco-auth__common-footer-button"
            >
              {t("action.login")}
            </Link>
          </div>
        </div>
      </FormView>
    </FormProvider>
  );
};

export default SignUp;
